var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { localized, msg } from '@lit/localize';
import { loadMaplibre, MAPLIBRE_CSS_URL } from './maplibre.js';
import * as GeoJSON from 'geojson';
let GeojsonMap = class GeojsonMap extends LitElement {
    constructor() {
        super(...arguments);
        this.mapOptions = {};
        this.geojson = null;
        this.mapLib = null;
        this.sourceAdded = false;
    }
    render() {
        const styleUrl = this.mapOptions.mapboxApiKey
            ? 'https://api.mapbox.com/mapbox-gl-js/v2.15.0/mapbox-gl.css'
            : MAPLIBRE_CSS_URL;
        return html `
      <link href="${styleUrl}" rel="stylesheet" />
      <div id="map">
        <slot></slot>
      </div>
    `;
    }
    async firstUpdated() {
        const container = this.renderRoot.querySelector('#map');
        const useMapbox = Boolean(this.mapOptions.mapboxApiKey);
        if (useMapbox) {
            this.mapLib = (await import('mapbox-gl')).default;
            this.mapLib.accessToken = this.mapOptions.mapboxApiKey;
        }
        else {
            this.mapLib = await loadMaplibre();
        }
        const style = this.mapOptions.style ??
            (useMapbox
                ? 'mapbox://styles/mapbox/streets-v11'
                : 'https://demotiles.maplibre.org/style.json');
        const options = {
            container,
            center: this.mapOptions.center ?? [2.3522, 48.8566],
            zoom: this.mapOptions.zoom ?? 4,
            pitch: this.mapOptions.pitch ?? 0,
            bearing: this.mapOptions.bearing ?? 0,
            interactive: this.mapOptions.interactive ?? true,
            style,
            ...this.mapOptions,
        };
        this.map = new this.mapLib.Map(options);
        // MapLibre v5: projection is not a constructor option, use setProjection()
        if (!useMapbox && this.mapOptions.projection && typeof this.map.setProjection === 'function') {
            const proj = typeof this.mapOptions.projection === 'string'
                ? { type: this.mapOptions.projection }
                : this.mapOptions.projection;
            this.map.once('style.load', () => {
                this.map.setProjection(proj);
            });
        }
        this.map.on('load', () => {
            this.addGeojsonLayer();
            this.dispatchEvent(new CustomEvent('map-loaded', {
                detail: { map: this.map, mapLib: this.mapLib },
            }));
        });
        this.map.on('click', (e) => {
            this.dispatchEvent(new CustomEvent('map-click', {
                detail: { lng: e.lngLat.lng, lat: e.lngLat.lat },
                bubbles: true,
                composed: true,
            }));
        });
    }
    updated(changedProps) {
        if (changedProps.has('geojson') && this.map) {
            if (this.map.isStyleLoaded()) {
                this.updateGeojsonLayer();
            }
            else {
                this.map.once('load', () => this.updateGeojsonLayer());
            }
        }
    }
    addGeojsonLayer() {
        if (!this.geojson || this.sourceAdded)
            return;
        this.map.addSource('geojson-source', {
            type: 'geojson',
            data: this.geojson,
        });
        // Add fill layer for polygons
        this.map.addLayer({
            id: 'geojson-fill',
            type: 'fill',
            source: 'geojson-source',
            filter: ['any', ['==', '$type', 'Polygon'], ['==', '$type', 'MultiPolygon']],
            paint: {
                'fill-color': '#088',
                'fill-opacity': 0.4,
            },
        });
        // Add outline layer for polygons
        this.map.addLayer({
            id: 'geojson-outline',
            type: 'line',
            source: 'geojson-source',
            filter: ['any', ['==', '$type', 'Polygon'], ['==', '$type', 'MultiPolygon']],
            paint: {
                'line-color': '#088',
                'line-width': 2,
            },
        });
        // Add line layer for LineStrings
        this.map.addLayer({
            id: 'geojson-line',
            type: 'line',
            source: 'geojson-source',
            filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'MultiLineString']],
            paint: {
                'line-color': '#f00',
                'line-width': 3,
            },
        });
        // Add circle layer for points
        this.map.addLayer({
            id: 'geojson-point',
            type: 'circle',
            source: 'geojson-source',
            filter: ['==', '$type', 'Point'],
            paint: {
                'circle-color': '#11b4da',
                'circle-radius': 6,
                'circle-stroke-width': 2,
                'circle-stroke-color': '#fff',
            },
        });
        this.sourceAdded = true;
        this.fitToBounds();
        this.setupPointClickHandler();
    }
    setupPointClickHandler() {
        // Click on points to show popup
        this.map.on('click', 'geojson-point', (e) => {
            if (!e.features || e.features.length === 0)
                return;
            const feature = e.features[0];
            if (!feature.geometry || feature.geometry.type !== 'Point')
                return;
            const coordinates = feature.geometry.coordinates.slice();
            const properties = feature.properties || {};
            // Handle anti-meridian wrapping
            while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
            }
            new this.mapLib.Popup()
                .setLngLat(coordinates)
                .setHTML(this.createPopupContent(properties))
                .addTo(this.map);
            this.dispatchEvent(new CustomEvent('feature-click', {
                detail: { feature, properties, coordinates },
                bubbles: true,
                composed: true,
            }));
        });
        // Change cursor on hover
        this.map.on('mouseenter', 'geojson-point', () => {
            this.map.getCanvas().style.cursor = 'pointer';
        });
        this.map.on('mouseleave', 'geojson-point', () => {
            this.map.getCanvas().style.cursor = '';
        });
    }
    createPopupContent(properties) {
        const entries = Object.entries(properties).filter(([, value]) => value !== null && value !== undefined);
        if (entries.length === 0) {
            return `<div style="padding: 8px;">${msg('No properties', { id: 'geojsonMap.noProperties', desc: 'Message shown in popup when a feature has no properties' })}</div>`;
        }
        const rows = entries
            .map(([key, value]) => `
        <tr>
          <td style="padding: 4px 8px; font-weight: 600; color: #333; border-bottom: 1px solid #eee;">${key}</td>
          <td style="padding: 4px 8px; color: #666; border-bottom: 1px solid #eee;">${value}</td>
        </tr>
      `)
            .join('');
        return `
      <div style="font-size: 13px; max-width: 300px; max-height: 250px; overflow: auto;">
        <table style="border-collapse: collapse; width: 100%;">
          ${rows}
        </table>
      </div>
    `;
    }
    updateGeojsonLayer() {
        if (!this.geojson)
            return;
        const source = this.map.getSource('geojson-source');
        if (source) {
            source.setData(this.geojson);
            this.fitToBounds();
        }
        else {
            this.addGeojsonLayer();
        }
    }
    fitToBounds() {
        if (!this.geojson)
            return;
        const bbox = this.calculateBbox(this.geojson);
        if (bbox) {
            this.map.fitBounds([
                [bbox[0], bbox[1]],
                [bbox[2], bbox[3]],
            ], { padding: 40, animate: true });
        }
    }
    calculateBbox(geojson) {
        const coords = [];
        const extractCoords = (geometry) => {
            switch (geometry.type) {
                case 'Point':
                    coords.push(geometry.coordinates);
                    break;
                case 'MultiPoint':
                case 'LineString':
                    coords.push(...geometry.coordinates);
                    break;
                case 'MultiLineString':
                case 'Polygon':
                    geometry.coordinates.forEach((ring) => coords.push(...ring));
                    break;
                case 'MultiPolygon':
                    geometry.coordinates.forEach((polygon) => polygon.forEach((ring) => coords.push(...ring)));
                    break;
                case 'GeometryCollection':
                    geometry.geometries.forEach(extractCoords);
                    break;
            }
        };
        if (geojson.type === 'FeatureCollection') {
            geojson.features.forEach((feature) => {
                if (feature.geometry)
                    extractCoords(feature.geometry);
            });
        }
        else if (geojson.type === 'Feature' && geojson.geometry) {
            extractCoords(geojson.geometry);
        }
        if (coords.length === 0)
            return null;
        let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
        coords.forEach(([lng, lat]) => {
            if (lng !== undefined && lat !== undefined) {
                minLng = Math.min(minLng, lng);
                minLat = Math.min(minLat, lat);
                maxLng = Math.max(maxLng, lng);
                maxLat = Math.max(maxLat, lat);
            }
        });
        return [minLng, minLat, maxLng, maxLat];
    }
    getMapInstance() {
        return this.map;
    }
    getMapLib() {
        return this.mapLib;
    }
};
GeojsonMap.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    #map {
      width: 100%;
      height: 100%;
    }
  `;
__decorate([
    property({ type: Object })
], GeojsonMap.prototype, "mapOptions", void 0);
__decorate([
    property({ type: Object })
], GeojsonMap.prototype, "geojson", void 0);
__decorate([
    state()
], GeojsonMap.prototype, "map", void 0);
GeojsonMap = __decorate([
    localized(),
    customElement('geojson-map')
], GeojsonMap);
export { GeojsonMap };
//# sourceMappingURL=GeojsonMap.js.map